/**
 * useCurrency — React hook for user-preference-aware currency formatting
 *
 * Reads the authenticated user's `preferences.currency` from the Redux store
 * and returns a pre-bound `fmt(amount)` formatter that uses `Intl.NumberFormat`
 * with the correct currency code and the browser's locale.
 *
 * Usage:
 *   const { currency, fmt } = useCurrency();
 *   // renders e.g. "Rp 1.500.000" for IDR or "$1,500.00" for USD
 *   return <span>{fmt(transaction.amount)}</span>;
 *
 * [T40P0] Currency change fix — previously all monetary values were hardcoded
 * to "$" + .toLocaleString('en-US') throughout every component, ignoring
 * the user's saved currency preference.
 */
import { useMemo } from 'react';
import { useAppSelector } from '@/features/hooks';
import { formatCurrency } from '@/lib/utils';

export interface UseCurrencyReturn {
  /** The ISO 4217 currency code from the user's preferences, e.g. "IDR", "USD" */
  currency: string;
  /**
   * Format a monetary amount using the user's preferred currency.
   * Automatically applies the correct locale symbol, decimal places,
   * and grouping separators for the currency (e.g. JPY → no decimals, IDR → Rp symbol).
   */
  fmt: (amount: number) => string;
}

export function useCurrency(): UseCurrencyReturn {
  const currency = useAppSelector(
    (state) => state.auth.user?.preferences?.currency ?? 'USD'
  );

  const fmt = useMemo(
    () => (amount: number) => formatCurrency(amount, currency),
    [currency]
  );

  return { currency, fmt };
}
